<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	import { tick } from 'svelte';
	// Stores
	import ACLStore from '@/Stores/ACLStore.js';
	import UserStore from '@/Stores/UserStore';
	// Partials
	import AddPromotion from '@/Partials/User/_AddPromotion.svelte';
	// Models
	import Promotion from '@/Models/Promotion.js';
	// Components
	import Prompt from '@/Components/Prompt.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import Icon from '@/Components/Icon.svelte';
	// Services
	import { notify } from '@/Services/Notify';
	// Constants
	import { UserRatingName, UserRatingPromoBlacklist } from '@/Constants/User.js';

	dayjs.extend(localizedFormat);

	export let user;

	$: promotions = (user && user.promotions && user.promotions.data && user.promotions.data.all()) ?? [];
	$: promoBlacklistedRating = UserRatingPromoBlacklist.includes(user.rating.id);

	let showAddPromotion = false;
	let showDeletePromotion = false;
	let deletePromotionId;

	const openDeletePromotion = async (promotionId) => {
		deletePromotionId = promotionId;
		showDeletePromotion = true;
		await tick();
		window.scrollTo(0, 0);
	};

	const deletePromotion = (promotionId) => {
		let promotion = new Promotion();
		promotion
			.deletePromotionFromUser(user.id, promotionId)
			.then(() => {
				UserStore.getUserWithDetails(user.id);
				notify({
					type: 'success',
					title: 'Promotion successfully removed!',
					message: `Promotion successfully removed from ${user.full_name}`,
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Error removing promotion from user',
					timeout: 3
				});
			})
			.finally(() => {
				showDeletePromotion = false;
				deletePromotionId = undefined;
			});
	};

	function onChangePromoDisabled(e) {
		UserStore.updatePromotionsDisabled(user.id, !e.target.checked)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'Promotions - ' + (e.target.checked ? 'ENABLED' : 'DISABLED'),
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update promotions',
					timeout: 5
				});
			});
	}

	function onChangeAutomatedPromotionsEnabled(e) {
		UserStore.updateAutomatedPromotionsEnabled(user.id, e.target.checked)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'Automated promotions (Campaigns) - ' + (e.target.checked ? 'ENABLED' : 'DISABLED'),
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update automated promotions',
					timeout: 5
				});
			});
	}
	function onChangeCashoutEnabled(e) {
		UserStore.updateCashoutEnabled(user.id, e.target.checked)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'User cashout ability - ' + (e.target.checked ? 'ENABLED' : 'DISABLED'),
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update user cashout ability',
					timeout: 5
				});
			});
	}
</script>

{#if $ACLStore && ACLStore.granted('promotions.create')}
	<AddPromotion bind:open={showAddPromotion} {user} />
{/if}

<Prompt
	bind:open={showDeletePromotion}
	title="Confirm delete"
	message={`Are you sure you want to delete this promotion from ${user.full_name}?`}
	onConfirm={() => deletePromotion(deletePromotionId)}
/>

{#if ACLStore.granted('promotions.read')}
	<div class="flex flex-col md:w-1/2 text-dark-grey bg-gray-50 min-h-48">
		<div class="flex items-center px-2 bg-dark-grey text-light text-xs md:text-base">
			<h2 class="flex-1 py-2 text-xl italic font-sans-condensed">Promotions</h2>
			<div
				class="flex flex-col md:flex-row items-start md:items-center justify-start md:justify-center space-y-1 md:space-y-0 md:space-x-2 ml-2 md:mr-2"
			>
				<CheckBox
					checked={user.cashout_enabled}
					text="Allow Cashout"
					className="text-xs"
					disabled={!ACLStore.granted('users.update')}
					on:change={onChangeCashoutEnabled}
				/>
				<CheckBox
					checked={user.automated_promotions_enabled}
					text="Allow Automated Promotions"
					disabled={!ACLStore.granted('users.update.promotions', true)}
					className="text-xs"
					on:change={onChangeAutomatedPromotionsEnabled}
				/>
				<span
					on:click={() =>
						ACLStore.granted('users.update.promotions', true) && promoBlacklistedRating
							? notify({
									type: 'warning',
									title: "Can't do that!",
									message: "Can't change this while rating is '" + UserRatingName[user.rating.id] + "'",
									timeout: 5
								})
							: ''}
				>
					<CheckBox
						checked={!user.promotions_disabled}
						disabled={promoBlacklistedRating || !ACLStore.granted('users.update.promotions', true)}
						text="Allow Promotions"
						className="text-xs"
						on:change={onChangePromoDisabled}
					/>
				</span>
			</div>
			{#if $ACLStore && ACLStore.granted('promotions.create')}
				<button
					disabled={promoBlacklistedRating || user.promotions_disabled}
					on:click={() => (showAddPromotion = true)}
					class="py-1 btn btn-primary focus:outline-none"
				>
					Add Promotion
				</button>
			{/if}
		</div>
		<div class="">
			<table class="table w-full overflow-y-auto">
				<tr class="text-sm text-left bg-grey text-light">
					<th class="py-2 pl-2">Name</th>
					<th class="py-2">Code</th>
					<th class="py-2">Type</th>
					<th class="py-2">Expires</th>
					<th class="py-2" />
				</tr>
				{#each promotions as promotion}
					<tr class="text-sm text-center">
						<td class="py-1 text-left border-b pl-2">
							{promotion.name}
						</td>
						<td class="py-1 text-left border-b">
							{promotion.code}
						</td>
						<td class="py-1 text-left border-b">
							{promotion.promotion_type.name}
						</td>
						<td class="py-1 text-left border-b">
							{dayjs(promotion.expired_at).format('D MMM YYYY, HH:mm')}
						</td>
						<td class="py-1 text-left border-b w-px pr-1">
							<button
								class="btn btn-danger flex flex-row justify-center items-center"
								on:click={() => {
									openDeletePromotion(promotion.id);
								}}
							>
								<Icon name="bin" classes="w-5 h-5" />
							</button>
						</td>
					</tr>
				{:else}
					<tr>
						<td colspan="5" class="text-md font-semibold text-center py-2"> User currently has no promotions </td>
					</tr>
				{/each}
			</table>
		</div>
	</div>
{/if}
